import { COST_SIMULATION_YEARS, pickT } from "@repo/shared";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { T } from "./settings-cost-chart-form.i18n";

type SettingsCostChartFormProps = {
  flowLanguage: string;
  displayMode: "rolling" | "calendar";
  onDisplayModeChange: (mode: "rolling" | "calendar") => void;
  chartType: "line" | "bar";
  onChartTypeChange: (type: "line" | "bar") => void;
  projectionYears: number;
  onProjectionYearsChange: (years: number) => void;
};

export function SettingsCostChartForm({
  flowLanguage,
  displayMode,
  onDisplayModeChange,
  chartType,
  onChartTypeChange,
  projectionYears,
  onProjectionYearsChange,
}: SettingsCostChartFormProps) {
  const t = pickT(T, flowLanguage);

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="cost-chart-display-mode">
          {t.costChartDisplayMode} <span className="text-destructive">{t.required}</span>
        </Label>
        <Select
          value={displayMode}
          onValueChange={(v) => onDisplayModeChange(v as "rolling" | "calendar")}
        >
          <SelectTrigger id="cost-chart-display-mode">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="rolling">{t.rolling12Months}</SelectItem>
            <SelectItem value="calendar">{t.calendarYear}</SelectItem>
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">{t.costChartHelp}</p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="cost-chart-type">
          {t.chartType} <span className="text-destructive">{t.required}</span>
        </Label>
        <Select value={chartType} onValueChange={(v) => onChartTypeChange(v as "line" | "bar")}>
          <SelectTrigger id="cost-chart-type">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="line">{t.lineChart}</SelectItem>
            <SelectItem value="bar">{t.barChart}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="cost-chart-projection-years">{t.projectionYears}</Label>
        <Input
          id="cost-chart-projection-years"
          type="number"
          min={2}
          max={COST_SIMULATION_YEARS}
          value={projectionYears}
          onChange={(e) => onProjectionYearsChange(Number(e.target.value))}
        />
        <p className="text-xs text-muted-foreground">{t.projectionYearsDesc}</p>
      </div>

      <div className="text-sm text-muted-foreground bg-muted p-3 rounded-md">
        <p className="font-medium mb-1">{t.costChartModeInfo}</p>
        <p>{displayMode === "rolling" ? t.costChartRollingDesc : t.costChartCalendarDesc}</p>
      </div>
    </div>
  );
}
